/**
 * Hook for providing DataSource dependencies
 * 
 * This hook provides the standard dependency objects that DataSources need,
 * following the dependency injection patterns.
 */

import { useServiceRegistry, useUserState, useRootStore, useAppState } from '../../stores/AppStateContext';
import { 
  BasicDataSourceDeps, 
  UserDataSourceDeps, 
  FullDataSourceDeps,
  StorageDataSourceDeps
} from '../services/types/DataSourceDependencies';

/**
 * Hook for basic DataSource dependencies (data client service + auth service)
 */
export const useBasicDataSourceDeps = (): BasicDataSourceDeps => {
  const serviceRegistry = useServiceRegistry();
  return {
    dataClientService: serviceRegistry.getDataClientService(),
    authService: serviceRegistry.getAuthService()
  };
};

/**
 * Hook for DataSources that need user context
 */
export const useUserDataSourceDeps = (): UserDataSourceDeps => {
  const serviceRegistry = useServiceRegistry();
  const userState = useUserState()!;
  return {
    dataClientService: serviceRegistry.getDataClientService(),
    authService: serviceRegistry.getAuthService(),
    userState
  };
};

/**
 * Hook for DataSources that need full app context
 */
export const useFullDataSourceDeps = (): FullDataSourceDeps => {
  const serviceRegistry = useServiceRegistry();
  const userState = useUserState()!;
  const rootStore = useRootStore();
  return {
    dataClientService: serviceRegistry.getDataClientService(),
    authService: serviceRegistry.getAuthService(),
    userState,
    rootStore,
  };
};

/**
 * Hook for DataSources that need storage service
 */
export const useStorageDataSourceDeps = (): StorageDataSourceDeps => {
  const serviceRegistry = useServiceRegistry();
  const userState = useUserState()!;
  const rootStore = useRootStore();
  return {
    dataClientService: serviceRegistry.getDataClientService(),
    authService: serviceRegistry.getAuthService(),
    storageService: serviceRegistry.getStorageService(),
    userState,
    rootStore,
  };
};